import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap-v5";
import { ExpertiseListModel } from "../../../hooks/partners/models/ExpertiseListModel";
import { getExpertiseDeclineReasons } from "../../../services/partnerExpertService";

// PR-I-87 — modal reusado para Approve/Decline en la moderacion de expertise-services.
// Sigue el mismo patron visual/UX que CommunityAdminActionModal para mantener consistencia con
// el resto de flujos de moderacion (introduction, community, partners). Aprovecha las clases CSS
// existentes de `_nb-community-admin.scss` para no duplicar estilos.
//
// Contrato backend (expertise/views.py AdminExpertiseServiceViewSet):
//   approve: PATCH /expertise/admin/expertise-services/<id>/approve/   (sin body)
//   reject:  PATCH /expertise/admin/expertise-services/<id>/reject/    body: { reason_id, note? }
// El dropdown de reasons viene de GET /expertise/decline-reasons/.

export type ExpertiseActionType = 'approve' | 'decline';

export type ExpertiseActionPayload = {
    reason_id: number;
    note: string;
}

type Props = {
    record: ExpertiseListModel | null;
    type: ExpertiseActionType;
    onClose: (result: boolean, payload?: ExpertiseActionPayload) => void;
}

type ReasonOption = { id: number; title: string };

export const ExpertiseActionModal = ({ record, type, onClose }: Props) => {
    const isMountedRef = useRef(true);
    const [reasons, setReasons] = useState<ReasonOption[]>([]);
    const [reasonId, setReasonId] = useState<number>(0);
    const [note, setNote] = useState<string>('');
    const [loadingReasons, setLoadingReasons] = useState(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; }
    }, []);

    useEffect(() => {
        if(type !== 'decline') return;
        (async () => {
            try {
                setLoadingReasons(true);
                const data = await getExpertiseDeclineReasons();
                if(!isMountedRef.current) return;
                // Backend puede devolver { results: [...] } (paginado) o array plano.
                const list: ReasonOption[] = Array.isArray(data) ? data : (data?.results ?? []);
                setReasons(list);
                if(list.length > 0) setReasonId(list[0].id);
            }
            finally {
                if(!isMountedRef.current) return;
                setLoadingReasons(false);
            }
        })();
    }, [type]);

    // Para decline exigimos reason (backend lo valida). Note es opcional; UI lo mantiene libre.
    const isValid = useMemo(() => {
        if(type === 'approve') return true;
        return reasonId > 0;
    }, [type, reasonId]);

    const handleConfirm = () => {
        if(!isValid) return;
        if(type === 'decline') {
            onClose(true, { reason_id: reasonId, note });
        }
        else {
            onClose(true);
        }
    };

    const title = type === 'decline' ? 'Decline Expertise' : 'Approve Expertise';
    const message = type === 'decline'
        ? `Are you sure you want to decline "${record?.title ?? 'this expertise'}"? The expert will be notified with the reason you choose.`
        : `Do you confirm publishing "${record?.title ?? 'this expertise'}"? It will become bookable immediately.`;

    return (
        <Modal show centered size="lg" backdrop="static" dialogClassName="admin-community-action-modal">
            <button className="close-modal" onClick={() => onClose(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M15.9998 29.3334C23.3332 29.3334 29.3332 23.3334 29.3332 16.0001C29.3332 8.66675 23.3332 2.66675 15.9998 2.66675C8.6665 2.66675 2.6665 8.66675 2.6665 16.0001C2.6665 23.3334 8.6665 29.3334 15.9998 29.3334Z" stroke="#B4B4B4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.2266 19.7734L19.7733 12.2268" stroke="#B4B4B4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.7733 19.7734L12.2266 12.2268" stroke="#B4B4B4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>

            <div className="admin-community-action-modal-content">
                <h3>{title}</h3>
                <span className="message">{message}</span>

                {type === 'decline' && (
                    <div className="admin-community-action-modal-content__reason">
                        <div>
                            <span className="title-section">DECLINE REASON</span>
                            <select
                                value={reasonId}
                                disabled={loadingReasons || reasons.length === 0}
                                onChange={(e) => setReasonId(Number(e.target.value))}
                            >
                                {reasons.length === 0 && (
                                    <option value={0}>{loadingReasons ? 'Loading...' : 'No reasons available'}</option>
                                )}
                                {reasons.map((r) => (
                                    <option key={r.id} value={r.id}>{r.title}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <span className="title-section">Note</span>
                            <input
                                value={note}
                                type="text"
                                maxLength={100}
                                onChange={(e) => setNote(e.target.value)}
                            />
                            <div className="meta-section">
                                <span>{note.length}/100</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="admin-community-action-modal-content__actions">
                    <button className="admin-community-action-modal-content__actions_secundary" onClick={() => onClose(false)}>
                        Cancel
                    </button>
                    <button
                        disabled={!isValid}
                        className="admin-community-action-modal-content__actions_primary"
                        onClick={handleConfirm}
                    >
                        {type === 'decline' ? 'Decline' : 'Approve'}
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                            <path d="M8.5 0.379395L12 3.38354H0" stroke="white"/>
                        </svg>
                    </button>
                </div>
            </div>
        </Modal>
    );
}
